// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@carbon/react';
import styles from './ReviewQueuePage.module.css';

type Domain = 'cooling' | 'power' | 'shared';
type TagType = 'cyan' | 'purple' | 'gray';

const DOMAIN_TAG_TYPE: Record<Domain, TagType> = {
  cooling: 'cyan',
  power: 'purple',
  shared: 'gray',
};

interface PendingRow {
  title: string;
  code: string;
  domain: Domain;
  equipment: string;
  version: string;
  updated: string;
}

/**
 * Static visual scaffold — the rows below are example content for the
 * mockup (the same two "In review" procedures shown on the Library screen),
 * not data from any service (see admin-center/README.md). Wiring Approve /
 * Reject to a real workflow is a follow-up PR.
 */
const ROWS: PendingRow[] = [
  {
    title: 'CDU Coolant Loop Leak Response and Isolation SOP',
    code: 'GA10-CDU-SOP-LEAK-RESPONSE-ISOLATION',
    domain: 'cooling',
    equipment: 'brick:Chiller',
    version: '1.0',
    updated: '2026-08-05',
  },
  {
    title: 'UPS Battery String Replacement Procedure',
    code: 'NA-US-GA10-OPS-SOP-40-2002-UPS-BATTERY-STRING-REPLACEMENT',
    domain: 'power',
    equipment: 'wattsch:UPSType',
    version: '1.0',
    updated: '2026-08-04',
  },
];

/**
 * Visual scaffold for Procedures > Review queue: procedures awaiting
 * sign-off, with Approve / Reject actions. Static placeholder — neither
 * button does anything yet.
 */
export default function ReviewQueuePage() {
  return (
    <div>
      <div className={styles.title}>Review queue</div>
      <div className={styles.resultCount}>{ROWS.length} results</div>

      <Table className={styles.table}>
        <TableHead>
          <TableRow>
            <TableHeader>Title</TableHeader>
            <TableHeader>Procedure #</TableHeader>
            <TableHeader>Domain</TableHeader>
            <TableHeader>Equipment</TableHeader>
            <TableHeader>Ver.</TableHeader>
            <TableHeader>Updated</TableHeader>
            <TableHeader aria-hidden="true" />
          </TableRow>
        </TableHead>
        <TableBody>
          {ROWS.map(row => (
            <TableRow key={row.code}>
              <TableCell className={styles.titleCell}>{row.title}</TableCell>
              <TableCell className={styles.codeCell}>{row.code}</TableCell>
              <TableCell>
                <Tag type={DOMAIN_TAG_TYPE[row.domain]} size="sm">
                  {row.domain}
                </Tag>
              </TableCell>
              <TableCell className={styles.equipmentCell}>{row.equipment}</TableCell>
              <TableCell>{row.version}</TableCell>
              <TableCell className={styles.updatedCell}>{row.updated}</TableCell>
              <TableCell className={styles.actionsCell}>
                <Button kind="danger--tertiary" size="sm" className={styles.rejectButton}>
                  Reject
                </Button>
                <Button kind="primary" size="sm" className={styles.approveButton}>
                  Approve
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
